import { compareNumbers } from "../utils/compareNumbers";
import {
  enumBranchPref,
  enumBranchStyle,
  enumBSFlagOptions,
  enumContainerType
} from "../constants";
import { NodeEntity } from "./NodeEntity";
import { compareHeight } from "../utils/compareHeight";

export class Optimizer {
  constructor() {
    this.fRootEntity = null;
    this.fPageLayoutMgr = null;
    this.fAllowSort = false;
    this.fSinglesTree = false;
    this.fBranchPref = enumBranchPref.tree;
    this.fDesiredAspectRatio = 1.4;
    this.fMinHeight = null; // OLD OLD
    this.fMinWidth = null; //  OLD OLD
    this.fEntityFitFound = false;
    this.fEntityCandidates = [];
    this.fKeys = [];
  }

  // Use the current page sizing to determine mins
  DetermineMinSize(pageLayoutMgr) {
    // Use the page size here
    var pp = pageLayoutMgr.GetPageProps();

    var horzMargins = pp.fMargins.fLeft + pp.fMargins.fRight;
    var vertMargins = pp.fMargins.fTop + pp.fMargins.fBottom + pp.fFooterHeight + pp.fHeaderHeight;

    var pageWidth = pp.fPageWidth - horzMargins;
    var pageHeight = pp.fPageHeight - vertMargins;
    //var autoSize = pp.fAutoSizePDF;

    this.fMinHeight = pageHeight;
    this.fMinWidth = pageWidth;

    var fudgeFactor = 1.0;
    this.fMinHeight = Math.round(this.fMinHeight * fudgeFactor);
    this.fMinWidth = Math.round(this.fMinWidth * fudgeFactor);
  }

  InitOptimizer(aNode, pageLayoutMgr, aspectRatio, allowSort, treeSingles, branchPrefs, docRef) {
    if (!aNode) {
      return;
    }

    this.fRootEntity = new NodeEntity();
    if (aNode.GetContainerNode()) {
      // there is a top head box here.
      this.fRootEntity.Initialize(aNode, null, pageLayoutMgr.fBranchMgr);
    } else {
      var parent = aNode.GetParent();
      this.fRootEntity.Initialize(aNode, parent, pageLayoutMgr.fBranchMgr);
    }

    this.fPageLayoutMgr = pageLayoutMgr;
    this.fAllowSort = allowSort;
    this.fSinglesTree = treeSingles;
    this.fBranchPref = branchPrefs;
    this.fDesiredAspectRatio = aspectRatio;
    this.fEntityFitFound = false;
    this.DetermineMinSize(pageLayoutMgr);
  }

  HasBranchStyles(docRef) {
    const chartTemplate = docRef?.GetProxyTemplate();
    const branchStyleRules = (chartTemplate?.fRuleList ?? []).filter(rule => {
      const branchStyleFlag = rule.GetBranchStyleFlag();
      return (
        branchStyleFlag !== null &&
        ![
          enumBSFlagOptions.assistleft,
          enumBSFlagOptions.assistright,
          enumBSFlagOptions.leftpeerassist,
          enumBSFlagOptions.rightpeerassist
        ].includes(branchStyleFlag)
      );
    });

    // Walk tree to check per-box overrides and rule matches
    if (!this.fRootEntity) return false;
    var stack = [this.fRootEntity];
    while (stack.length > 0) {
      var nodeEntity = stack.pop();
      if (!nodeEntity) continue;
      var chartBox = nodeEntity.fNode?.GetChartBox?.();
      if (chartBox) {
        // Check per-box manual override
        var boxLayout = chartBox.GetLayout();
        var overrideBranchStyle = boxLayout?.GetOverridesKey("bstyle", null);
        if (
          overrideBranchStyle != null &&
          ![
            enumBranchStyle.assistantpeerright,
            enumBranchStyle.assistantpeerleft,
            enumBranchStyle.assistantright,
            enumBranchStyle.assistantleft
          ].includes(overrideBranchStyle)
        ) {
          return true;
        }
        // Check rule matches
        var dataObj = chartBox.GetDataObj();
        if (dataObj != null) {
          for (var r = 0; r < branchStyleRules.length; r++) {
            if (branchStyleRules[r].MatchRule(dataObj, docRef)) {
              return true;
            }
          }
        }
      }

      var subs = nodeEntity.GetChildren();
      for (var i = subs.length - 1; i >= 0; i--) {
        stack.push(subs[i]);
      }
    }

    return false;
  }

  getEffectiveAspectRatio(entity) {
    var height = entity.GetHeight();
    var width = entity.GetWidth();
    var effectiveAR = Math.max(width, this.fMinWidth) / Math.max(height, this.fMinHeight);
    return effectiveAR;
  }

  isAFitCandidate(entity) {
    var isFit = false;

    if (entity.GetHeight() <= this.fMinHeight && entity.GetWidth() <= this.fMinWidth) {
      isFit = true;
    }

    return isFit;
  }

  isEntityBetterCandidate(aspectRatio, entity) {
    var newAspectRatio = this.getEffectiveAspectRatio(entity);
    var ratio1 =
      aspectRatio > this.fDesiredAspectRatio
        ? aspectRatio / this.fDesiredAspectRatio
        : this.fDesiredAspectRatio / aspectRatio;
    var ratio2 =
      newAspectRatio > this.fDesiredAspectRatio
        ? newAspectRatio / this.fDesiredAspectRatio
        : this.fDesiredAspectRatio / newAspectRatio;

    var isBetter = ratio2 <= ratio1;

    return isBetter;
  }

  isEntityCloseEnough(entity) {
    var isClose = false;
    var aspectRatio = this.getEffectiveAspectRatio(entity);
    if (
      aspectRatio / this.fDesiredAspectRatio > 0.9 &&
      aspectRatio / this.fDesiredAspectRatio < 1.1
    ) {
      isClose = true;
    }

    return isClose;
  }

  PerformOperations(param, selectedNode) {
    if (param === false) {
      param = "";
    }

    if (param.length === 0) {
      //param = "2";
      param = "1,7,6";
    }

    var functionIDs = param.split(",");
    if (functionIDs.length === 0) {
      return;
    }

    var firstFunction = functionIDs[0];
    if (firstFunction.indexOf("a") > -1) {
      this.fDesiredAspectRatio = parseFloat(firstFunction.substr(1));
      functionIDs.shift();
    }

    var selectedEntity = this.fRootEntity;
    if (selectedNode) {
      selectedEntity = this.fRootEntity.GetEntityByID(selectedNode.GetID());
    }

    // this is to revert back to starting
    var startingBranchStyle = selectedEntity.fSubBranchStyle;
    var startingSubs = [];
    var nSubs = selectedEntity.NumChildren("Subordinate");
    for (var i = 0; i < nSubs; i++) {
      var newSub = selectedEntity.GetEntity("Subordinate", i).Clone();
      startingSubs.push(newSub);
    }
    selectedEntity.ReAssignCoordinates(true);
    var startedEntity = selectedEntity.Clone();

    for (i = 0; i < functionIDs.length; i++) {
      var recursive = false;
      var functionID = functionIDs[i];
      if (functionID.indexOf("r") > -1) {
        recursive = true;
        functionID = functionID.slice(0, functionID.length - 1);
      }

      if (this.fEntityFitFound) {
        // This selected entity already fits the page. Done. Get out.
        break;
      }
      switch (functionID) {
        case "1":
          this.Method1(selectedEntity);
          break;

        case "2":
          this.BalanceMultiColumn(selectedEntity, recursive);
          break;

        case "3":
          this.increaseNumColumnStyle(selectedEntity, recursive);
          break;

        case "4":
          this.decreaseNumColumnStyle(selectedEntity, recursive);
          break;

        case "5":
          this.ShortenEntity(selectedEntity);
          break;

        case "6":
          selectedEntity.ReAssignCoordinates(true);
          if (!this.isEntityCloseEnough(selectedEntity)) {
            this.ReorderTreeStyle(selectedEntity, recursive);
          }
          break;

        case "7":
          this.Method7(selectedEntity);
          break;

        case "8":
          this.ReorderLastEntities(selectedEntity);
          break;

        case "9":
          this.ReorderTreeStyle(selectedEntity);

        default:
          break;
      }
    }

    selectedEntity.ReAssignCoordinates(true);

    // If nothing has changed width wise, revert back. This is a questionable act
    if (selectedEntity.GetWidth() === startedEntity.GetWidth() && false) {
      selectedEntity = startedEntity.Clone();
      var entitySubs = selectedEntity.GetEntities("Subordinate");
      this.UpdateLayoutIndices(this.fPageLayoutMgr, selectedEntity, entitySubs, true);
    } else {
      entitySubs = selectedEntity.GetEntities("Subordinate");
      this.UpdateLayoutIndices(this.fPageLayoutMgr, selectedEntity, entitySubs, true);
    }

    //console.log("Aspect Ratio", this.getEffectiveAspectRatio(selectedEntity));
  }

  // This is method 1 of optimization, it:
  //  - keeps the second level to be tree-style
  //  - makes all other levels to be one-column
  //     - exception: makes single entities to be tree if it is the preference by caller
  Method1(entity) {
    var branchStyleArray = [];
    branchStyleArray.push(enumBranchStyle.tree);
    branchStyleArray.push(enumBranchStyle.onecolumnright);

    if (this.fSinglesTree) {
      var lastBranchStyle = enumBranchStyle.tree;
      this.SetAllBranchStyle(entity, branchStyleArray, lastBranchStyle);
    } else {
      this.SetAllBranchStyle(entity, branchStyleArray);
    }

    entity.ReAssignCoordinates(true);
  }

  // Method 7 is what the optimization heart and center is. It makes second level tree-style, and subsequent
  // ones one-column one
  // sees if it needs to shorten sub entities to get to desired aspect ratio
  Method7(entity) {
    // first and foremost singles.
    if (this.fSinglesTree) {
      this.SetSingleStyles(entity, enumBranchStyle.tree, true);
    }

    // Technically, this needs to happen only if a box has changed. But for now, we are
    // crudely re-assigning the coords
    entity.ReAssignCoordinates(true);

    // special case of one sub and subsequent level having more than one entity. There is
    // a desire to make subsequent entities behave like this one.
    if (
      entity.NumChildren("Subordinate") === 1 &&
      entity.GetEntity("Subordinate", 0).NumChildren("Subordinate") > 1
    ) {
      var subEntity = entity.GetEntity("Subordinate", 0);
      this.Method7(subEntity);
      return;
    }

    // If too many boxes exist (as determined by area of boxes), do not bother try to find
    // a fit. Otherwise, first try to find a fit.
    var areaRatio = this.fRootEntity.getTotalBoxAreas() / (this.fMinWidth * this.fMinHeight);
    if (areaRatio < 0.4) {
      // 0.4 is an empirically derived number.

      this.shortenToFit(entity);
      // if we found a fit entity, we are done
      if (this.fEntityFitFound) {
        return;
      }

      // Now that we didn't find a fit candidate, we need to see if best aspect ratio is acceptable.
      if (this.fEntityCandidates.length > 0) {
        var bestEntity = this.fEntityCandidates[0];
        if (this.isEntityCloseEnough(bestEntity)) {
          // we have found a close enough candidate. yay
          var subs = bestEntity.GetEntities("Subordinate").slice(0);
          entity.ReplaceSubs(subs);
          return;
        }
      }
    }

    var secondLevelCandidates = this.GetCandidateStylesList(entity);

    var done = false;
    var entityCopy = null;
    var bestRatio = Number.POSITIVE_INFINITY;

    while (!done && secondLevelCandidates.length > 0) {
      entityCopy = entity.Clone();
      var secondLevelBranchStyle = secondLevelCandidates.shift();
      entityCopy.SetSubBranchStyle(secondLevelBranchStyle);

      entityCopy.ReAssignCoordinates(false);
      this.ReorderTreeStyle(entityCopy, false);
      this.BalanceMultiColumn(entityCopy, true);
      entityCopy.ReAssignCoordinates(true);
      if (this.isEntityBetterCandidate(bestRatio, entityCopy)) {
        bestEntity = entityCopy.Clone();
        bestRatio = this.getEffectiveAspectRatio(bestEntity);
        if (this.isEntityCloseEnough(bestEntity)) {
          done = true;
        }
      }

      var tryShorteningCount = 0;
      var aspectRatio = 0;
      while (!done && aspectRatio < this.fDesiredAspectRatio && tryShorteningCount++ < 20) {
        // Let's shorten the entity in hope of getting it fit to the page, or finding a better aspect ration
        this.ShortenEntity(entityCopy);
        this.ReorderLastEntities(entityCopy);
        this.ReorderTreeStyle(entityCopy, true);
        entityCopy.ReAssignCoordinates(true);
        aspectRatio = this.getEffectiveAspectRatio(entityCopy);

        if (this.isEntityBetterCandidate(bestRatio, entityCopy)) {
          bestEntity = entityCopy.Clone();
          bestRatio = this.getEffectiveAspectRatio(bestEntity);
          if (this.isEntityCloseEnough(bestEntity)) {
            done = true;
          }
        }
      }
    }

    // after all the loop, bestEntity should be best candidate of Method7
    subs = bestEntity.GetEntities("Subordinate").slice(0);
    entity.ReplaceSubs(subs);
  }

  // Shorten To Fit is what the optimization heart and center is. It makes second level tree-style, and subsequent
  // ones one-column one
  // sees if it needs to shorten sub entities to get to desired aspect ratio
  shortenToFit(entity) {
    var secondLevelCandidates = this.GetCandidateStylesList(entity);

    var entityCopy = null;
    var bestEntity = null;
    var bestRatio = Number.POSITIVE_INFINITY;

    while (!this.fEntityFitFound && secondLevelCandidates.length > 0) {
      entityCopy = entity.Clone();
      var secondLevelBranchStyle = secondLevelCandidates.shift();
      entityCopy.SetSubBranchStyle(secondLevelBranchStyle);
      entityCopy.ReAssignCoordinates(false);
      this.ReorderTreeStyle(entityCopy, false);
      this.BalanceMultiColumn(entityCopy, true);
      this.ReorderLastEntities(entityCopy);
      entityCopy.ReAssignCoordinates(true);
      if (this.isAFitCandidate(entityCopy)) {
        this.fEntityFitFound = true;
      } else if (this.isEntityBetterCandidate(bestRatio, entityCopy)) {
        bestEntity = entityCopy.Clone();
        bestRatio = this.getEffectiveAspectRatio(bestEntity);
      }

      var tryShorteningCount = 0;
      var entityWidth = entityCopy.GetWidth();
      while (!this.fEntityFitFound && entityWidth < this.fMinWidth && tryShorteningCount++ < 20) {
        // Let's shorten the entity in hope of getting it fit to the page,
        // along the way, if a better aspect ratio is found keep a note of it
        this.ShortenEntity(entityCopy);
        this.ReorderLastEntities(entityCopy);
        this.ReorderTreeStyle(entityCopy, true);
        entityCopy.ReAssignCoordinates(true);
        entityWidth = entityCopy.GetWidth();

        if (this.isAFitCandidate(entityCopy)) {
          this.fEntityFitFound = true;
        } else if (this.isEntityBetterCandidate(bestRatio, entityCopy)) {
          bestEntity = entityCopy.Clone();
          bestRatio = this.getEffectiveAspectRatio(bestEntity);
        }
      }
    }

    // after all the loop, bestEntity should be best candidate
    if (this.fEntityFitFound) {
      var subs = entityCopy.GetEntities("Subordinate").slice(0);
      entity.ReplaceSubs(subs);
    } else {
      this.fEntityCandidates.length = 0;
      this.fEntityCandidates.push(bestEntity);
    }
  }

  ReorderLastEntities(entity) {
    // first and foremost if fAllowSort is false, this should be a no-op operation
    if (!this.fAllowSort) {
      return;
    }

    if (entity.isMultiColumnStyle()) {
      var ix = this.LastRowIndex(entity);
      if (ix.lastMinusOne.length > 0) {
        var combinedIndex = ix.lastMinusOne.concat(ix.last);
        this.ResortByHeightAndIndex(entity, combinedIndex);
      }
    }
  }

  // recursively goes through entity and its subordinates to set the branchStyles accordingly
  SetAllBranchStyle(entity, branchStyleArray, lastBranchStyle) {
    var k = branchStyleArray.length > 1 ? 1 : 0;
    var branchStyleArray2 = branchStyleArray.slice(k);

    var subs = entity.GetEntities("Subordinate");
    for (var i = 0; i < subs.length; i++) {
      if (typeof lastBranchStyle === "undefined") {
        this.SetAllBranchStyle(subs[i], branchStyleArray2);
      } else {
        this.SetAllBranchStyle(subs[i], branchStyleArray2, lastBranchStyle);
      }
    }

    if (typeof lastBranchStyle === "undefined") {
      lastBranchStyle = branchStyleArray[0];
    }

    var branchStyle = branchStyleArray[0];
    if (subs.length <= 1) {
      branchStyle = lastBranchStyle;
    }

    entity.SetSubBranchStyle(branchStyle);
  }

  SetSingleStyles(entity, branchStyle, recursive) {
    if (recursive) {
      var subs = entity.GetEntities("Subordinate");
      for (var i = 0; i < subs.length; i++) {
        this.SetSingleStyles(subs[i], branchStyle, recursive);
      }
    }

    if (subs.length <= 1) {
      entity.SetSubBranchStyle(branchStyle);
    }
  }

  increaseNumColumnStyle(entity, recursive) {
    if (recursive) {
      var subs = entity.GetEntities("Subordinate");
      for (var i = 0; i < subs.length; i++) {
        this.increaseNumColumnStyle(subs[i], recursive);
      }
    }

    var newBranchStyle = this.nextMultiColumnStyle(entity);
    if (newBranchStyle !== -1) {
      entity.SetSubBranchStyle(newBranchStyle);
    }
  }

  decreaseNumColumnStyle(entity, recursive) {
    if (recursive) {
      var subs = entity.GetEntities("Subordinate");
      for (var i = 0; i < subs.length; i++) {
        this.decreaseNumColumnStyle(subs[i], recursive);
      }
    }

    var newBranchStyle = this.prevMultiColumnStyle(entity);
    if (newBranchStyle !== -1) {
      entity.SetSubBranchStyle(newBranchStyle);
    }
  }

  // Based on preferences that is passed, it creates a list of candidates
  // to try on.
  // if style is
  GetCandidateStylesList(entity) {
    var branchStyles = [];
    if (entity.NumChildren("Subordinate") < 2) {
      if (this.fSinglesTree) {
        branchStyles.push(enumBranchStyle.tree);
      } else {
        branchStyles.push(entity.fSubBranchStyle);
      }

      return branchStyles;
    }

    // put criteria for reasonable styles based
    var numSubs = entity.NumChildren("Subordinate");
    var okTree = numSubs < 50;
    var okOneColumn = true;
    var okTwoColumn = numSubs > 2;
    var okFourColumn = numSubs > 4;
    var okSixColumn = numSubs > 6;
    var okEightColumn = numSubs > 8;
    var okTwelveColumn = numSubs > 12;
    var okSixteenColumn = numSubs > 16;
    var okTwentyColumn = numSubs > 20;

    switch (this.fBranchPref) {
      case enumBranchPref.tree:
        if (okTree) branchStyles.push(enumBranchStyle.tree);
        if (okTwentyColumn) branchStyles.push(enumBranchStyle.twentycolumn);
        if (okSixteenColumn) branchStyles.push(enumBranchStyle.sixteencolumn);
        if (okTwelveColumn) branchStyles.push(enumBranchStyle.twelvecolumn);
        if (okEightColumn) branchStyles.push(enumBranchStyle.eightcolumn);
        if (okSixColumn) branchStyles.push(enumBranchStyle.sixcolumn);
        if (okFourColumn) branchStyles.push(enumBranchStyle.fourcolumn);
        if (okTwoColumn) branchStyles.push(enumBranchStyle.twocolumn);
        if (okOneColumn) branchStyles.push(enumBranchStyle.onecolumnright);
        break;

      case enumBranchPref.twocolumn:
        if (okTwoColumn) branchStyles.push(enumBranchStyle.twocolumn);
        if (okOneColumn) branchStyles.push(enumBranchStyle.onecolumnright);
        if (okFourColumn) branchStyles.push(enumBranchStyle.fourcolumn);
        if (okSixColumn) branchStyles.push(enumBranchStyle.sixcolumn);
        if (okEightColumn) branchStyles.push(enumBranchStyle.eightcolumn);
        if (okTwelveColumn) branchStyles.push(enumBranchStyle.twelvecolumn);
        if (okSixteenColumn) branchStyles.push(enumBranchStyle.sixteencolumn);
        if (okTwentyColumn) branchStyles.push(enumBranchStyle.twentycolumn);
        if (okTree) branchStyles.push(enumBranchStyle.tree);
        break;

      case enumBranchPref.fourcolumn:
        if (okFourColumn) branchStyles.push(enumBranchStyle.fourcolumn);
        if (okSixColumn) branchStyles.push(enumBranchStyle.sixcolumn);
        if (okTwoColumn) branchStyles.push(enumBranchStyle.twocolumn);
        if (okEightColumn) branchStyles.push(enumBranchStyle.eightcolumn);
        if (okOneColumn) branchStyles.push(enumBranchStyle.onecolumnright);
        if (okTwelveColumn) branchStyles.push(enumBranchStyle.twelvecolumn);
        if (okSixteenColumn) branchStyles.push(enumBranchStyle.sixteencolumn);
        if (okTwentyColumn) branchStyles.push(enumBranchStyle.twentycolumn);
        if (okTree) branchStyles.push(enumBranchStyle.tree);
        break;

      default:
        branchStyles.push(entity.fSubBranchStyle);
        if (okTree) branchStyles.push(enumBranchStyle.tree);
        if (okOneColumn) branchStyles.push(enumBranchStyle.onecolumnright);
        if (okTwoColumn) branchStyles.push(enumBranchStyle.twocolumn);
        if (okFourColumn) branchStyles.push(enumBranchStyle.fourcolumn);
        if (okSixColumn) branchStyles.push(enumBranchStyle.sixcolumn);
        if (okEightColumn) branchStyles.push(enumBranchStyle.eightcolumn);
        break;
    }

    return branchStyles;
  }

  // next style that increases number of columns hence leading to shorter
  // and fatter style
  nextMultiColumnStyle(entity) {
    var branchStyle = entity.fSubBranchStyle;
    var numSubs = entity.NumChildren("Subordinate");
    var nextBranchStyle = -1;
    switch (branchStyle) {
      case enumBranchStyle.onecolumncenterright:
      case enumBranchStyle.onecolumnright:
      case enumBranchStyle.onecolumnleft:
      case enumBranchStyle.onecolumncenterleft:
        if (numSubs > 2) {
          nextBranchStyle = enumBranchStyle.twocolumn;
        } else {
          nextBranchStyle = enumBranchStyle.tree;
        }
        break;

      case enumBranchStyle.twocolumn:
        if (numSubs > 4) {
          nextBranchStyle = enumBranchStyle.fourcolumn;
        } else {
          nextBranchStyle = enumBranchStyle.tree;
        }
        break;

      case enumBranchStyle.fourcolumn:
        if (numSubs > 6) {
          nextBranchStyle = enumBranchStyle.sixcolumn;
        } else {
          nextBranchStyle = enumBranchStyle.tree;
        }
        break;

      case enumBranchStyle.sixcolumn:
        if (numSubs > 8) {
          nextBranchStyle = enumBranchStyle.eightcolumn;
        } else {
          nextBranchStyle = enumBranchStyle.tree;
        }
        break;

      case enumBranchStyle.eightcolumn:
        if (numSubs > 12) {
          nextBranchStyle = enumBranchStyle.twelvecolumn;
        } else {
          nextBranchStyle = enumBranchStyle.tree;
        }
        break;

      case enumBranchStyle.twelvecolumn:
        if (numSubs > 16) {
          nextBranchStyle = enumBranchStyle.sixteencolumn;
        } else {
          nextBranchStyle = enumBranchStyle.tree;
        }
        break;

      case enumBranchStyle.sixteencolumn:
        if (numSubs > 20) {
          nextBranchStyle = enumBranchStyle.twentycolumn;
        } else {
          nextBranchStyle = enumBranchStyle.tree;
        }
        break;

      case enumBranchStyle.twentycolumn:
        nextBranchStyle = enumBranchStyle.tree;
        break;

      default:
        break;
    }

    return nextBranchStyle;
  }

  prevMultiColumnStyle(entity) {
    var branchStyle = entity.fSubBranchStyle;
    var numSubs = entity.NumChildren("Subordinate");
    var prevBranchStyle = -1;

    switch (branchStyle) {
      case enumBranchStyle.twocolumn:
        prevBranchStyle = enumBranchStyle.onecolumnright;
        break;

      case enumBranchStyle.fourcolumn:
        if (numSubs > 2) {
          prevBranchStyle = enumBranchStyle.twocolumn;
        } else {
          prevBranchStyle = enumBranchStyle.onecolumnright;
        }
        break;

      case enumBranchStyle.sixcolumn:
        if (numSubs > 4) {
          prevBranchStyle = enumBranchStyle.fourcolumn;
        } else if (numSubs > 2) {
          prevBranchStyle = enumBranchStyle.twocolumn;
        } else {
          prevBranchStyle = enumBranchStyle.onecolumnright;
        }
        break;

      case enumBranchStyle.eightcolumn:
        if (numSubs > 6) {
          prevBranchStyle = enumBranchStyle.sixcolumn;
        } else if (numSubs > 4) {
          prevBranchStyle = enumBranchStyle.fourcolumn;
        } else if (numSubs > 2) {
          prevBranchStyle = enumBranchStyle.twocolumn;
        } else {
          prevBranchStyle = enumBranchStyle.onecolumnright;
        }
        break;

      case enumBranchStyle.twelvecolumn:
        if (numSubs > 8) {
          prevBranchStyle = enumBranchStyle.eightcolumn;
        } else if (numSubs > 6) {
          prevBranchStyle = enumBranchStyle.sixcolumn;
        } else if (numSubs > 4) {
          prevBranchStyle = enumBranchStyle.fourcolumn;
        } else if (numSubs > 2) {
          prevBranchStyle = enumBranchStyle.twocolumn;
        } else {
          prevBranchStyle = enumBranchStyle.onecolumnright;
        }
        break;

      case enumBranchStyle.sixteencolumn:
        if (numSubs > 12) {
          prevBranchStyle = enumBranchStyle.twelvecolumn;
        } else if (numSubs > 8) {
          prevBranchStyle = enumBranchStyle.eightcolumn;
        } else if (numSubs > 6) {
          prevBranchStyle = enumBranchStyle.sixcolumn;
        } else if (numSubs > 4) {
          prevBranchStyle = enumBranchStyle.fourcolumn;
        } else if (numSubs > 2) {
          prevBranchStyle = enumBranchStyle.twocolumn;
        } else {
          prevBranchStyle = enumBranchStyle.onecolumnright;
        }
        break;

      case enumBranchStyle.twentycolumn:
        if (numSubs > 16) {
          prevBranchStyle = enumBranchStyle.sixteencolumn;
        } else if (numSubs > 12) {
          prevBranchStyle = enumBranchStyle.twelvecolumn;
        } else if (numSubs > 8) {
          prevBranchStyle = enumBranchStyle.eightcolumn;
        } else if (numSubs > 6) {
          prevBranchStyle = enumBranchStyle.sixcolumn;
        } else if (numSubs > 4) {
          prevBranchStyle = enumBranchStyle.fourcolumn;
        } else if (numSubs > 2) {
          prevBranchStyle = enumBranchStyle.twocolumn;
        } else {
          prevBranchStyle = enumBranchStyle.onecolumnright;
        }
        break;

      case enumBranchStyle.tree:
        if (numSubs > 20) {
          prevBranchStyle = enumBranchStyle.twentycolumn;
        } else if (numSubs > 16) {
          prevBranchStyle = enumBranchStyle.sixteencolumn;
        } else if (numSubs > 12) {
          prevBranchStyle = enumBranchStyle.twelvecolumn;
        } else if (numSubs > 8) {
          prevBranchStyle = enumBranchStyle.eightcolumn;
        } else if (numSubs > 6) {
          prevBranchStyle = enumBranchStyle.sixcolumn;
        } else if (numSubs > 4) {
          prevBranchStyle = enumBranchStyle.fourcolumn;
        } else if (numSubs > 2) {
          prevBranchStyle = enumBranchStyle.twocolumn;
        } else {
          prevBranchStyle = enumBranchStyle.onecolumnright;
        }
        break;

      default:
        break;
    }

    return prevBranchStyle;
  }

  MakeSameStyle(nodeEntity, style, recursive) {
    if (!nodeEntity) return;

    // make all non-assistant subordinates to be style
    var numSubs = nodeEntity.NumChildren("Subordinate");
    if (numSubs > 0) {
      nodeEntity.SetSubBranchStyle(style);
    }

    // repeat this for all children if recursive is true
    if (recursive) {
      var subs = nodeEntity.GetEntities("Subordinate");
      for (var i = 0; i < subs.length; i++) {
        this.MakeSameStyle(subs[i], style, recursive);
      }
    }
  }

  updateNodeStyle(nodeEntity, recursive) {
    if (typeof nodeEntity === "undefined") {
      nodeEntity = this.fRootEntity;
    }

    if (typeof recursive === "undefined") {
      recursive = true;
    }

    if (!nodeEntity) return;

    var aNode = nodeEntity.fNode;
    var style = nodeEntity.fBranchStyle;

    var existingStyle = aNode.GetBranchStyle();
    // kb 5/11/2022
    // or condition added to ensure containers always enter this block and
    // assign appropriate branch style to their members
    if (aNode.GetParent() || aNode.GetLayout().GetContainerType() !== enumContainerType.nonmrb) {
      if (style !== existingStyle) {
        //console.log('Updating Branch style from ' + existingStyle + 'to: ' + style);
        aNode.SetBranchStyle(style);
        // kb 5/11/2022
        // ensuring container members are assigned the same branch style as container node
        // containers are initialized w/ tree style
        var insideSubs = nodeEntity.GetEntities("InsideChild");
        for (var i = 0; i < insideSubs.length; i++) {
          insideSubs[i].fNode.SetBranchStyle(style);
        }
      }
    }

    if (recursive) {
      var subs = nodeEntity.GetEntities("Subordinate");
      for (var i = 0; i < subs.length; i++) {
        this.updateNodeStyle(subs[i], recursive);
      }
    }
  }

  // starts from entity and traverses to all children to make the ones that have one sub only tree-style
  MakeSinglesTreeStyle(entity) {
    if (typeof entity === "undefined") {
      entity = this.fRootEntity;
    }

    if (!entity) return;

    var subs = entity.GetEntities("Subordinate");
    if (subs.length === 1) {
      entity.SetSubBranchStyle(enumBranchStyle.tree);
    }

    for (var i = 0; i < subs.length; i++) {
      this.MakeSinglesTreeStyle(subs[i]);
    }
  }

  SortSubsByHeight(entity, recursive) {
    if (recursive) {
      var subs = entity.GetEntities("Subordinate");
      for (var i = 0; i < subs.length; i++) {
        this.SortSubsByHeight(subs[i], entity);
      }
    }

    if (entity.fSubBranchStyle !== enumBranchStyle.tree || entity.NumChildren("Subordinate") < 2) {
      return;
    }

    var subHeights = entity.GetSubHeights();
    var sortedIndex = this.GetIndexedSortByGrouping(subHeights, 1);
    entity.ResortSubsByIndex(sortedIndex);
  }

  ResortByHeightAndIndex(entity, subIndex) {
    var subHeights = [];
    var nSubs = entity.NumChildren("Subordinate");
    for (var i = 0; i < subIndex.length; i++) {
      if (subIndex[i] >= 0 && subIndex[i] < nSubs) {
        var sub = entity.GetEntity("Subordinate", subIndex[i]);
        subHeights.push({ index: subIndex[i], value: sub.GetHeight() });
      } else {
        // something is wrong with the index. return
        return;
      }
    }

    var sortedIndexTmp = this.GetIndexedSortByGrouping(subHeights, 1);

    var sortedIndex = [];
    for (i = 0; i < nSubs - subIndex.length; i++) {
      sortedIndex.push(i);
    }

    for (i = 0; i < sortedIndexTmp.length; i++) {
      sortedIndex.push(sortedIndexTmp[i]);
    }

    entity.ResortSubsByIndex(sortedIndex);
  }

  ReorderTreeStyle(entity, recursive) {
    // first and foremost, if fAllowSort is false, this is a no-op function
    if (!this.fAllowSort) {
      return;
    }

    // If it is recursive, try children recursively
    var subs = entity.GetEntities("Subordinate");
    if (recursive) {
      for (var i = 0; i < subs.length; i++) {
        this.ReorderTreeStyle(subs[i], entity);
      }
    }

    var numSubs = subs.length;
    if (entity.fSubBranchStyle !== enumBranchStyle.tree || numSubs < 2 || numSubs > 40) {
      return;
    }

    //console.log("Reorder Tree Style: " + entity.NumChildren());
    var initialWidth = entity.GetWidth();

    var singlePolys = [];
    var nonSinglePolys = [];
    entity.getSubPolys(nonSinglePolys, singlePolys);

    var bestIndArr = [];
    var betterOrderFound = this.bestOrderIndexForTreestyle(
      bestIndArr,
      nonSinglePolys,
      singlePolys,
      initialWidth
    );

    if (betterOrderFound) {
      // bestIndArr is the best order. Traverse through it and get sub indices
      var subsBest = [];
      for (i = 0; i < bestIndArr.length; i++) {
        var newSub = entity.GetEntity("Subordinate", bestIndArr[i]).Clone();
        subsBest.push(newSub);
      }

      entity.ReplaceSubs(subsBest);
    }
  }

  // tries to inserts singles in between non-singles such that it leads to least width possible
  bestOrderIndexForTreestyle(indexArr, nonSinglePolys, singlePolys, initialWidth) {
    // empty array if needed
    indexArr.length = 0;

    if (nonSinglePolys.length === 0 || singlePolys.length === 0) {
      return false;
    }

    var initialSinglePolys = singlePolys.slice(0);
    var IP = [];
    for (var i = 0; i <= nonSinglePolys.length; i++) {
      IP.push(i);
    }

    var betterWidthFound = false;
    var combinedPolys = nonSinglePolys.slice();
    while (singlePolys.length > 0) {
      var grabbedElement = singlePolys.shift();
      var bestWidth = initialWidth;
      var bestInsertIndex = -1;
      var bestPolys = [];
      for (i = 0; i < IP.length; i++) {
        var tempPolys = combinedPolys.slice();
        var insertPosition = IP[i];
        tempPolys.splice(insertPosition, 0, grabbedElement);
        var newWidth = this.getJuxtaposedWidth(tempPolys);
        var newCandidateFound = false;
        if (newWidth < bestWidth) {
          newCandidateFound = true;
          betterWidthFound = true;
        }

        if (newWidth === bestWidth) {
          if (bestInsertIndex === 0) {
            newCandidateFound = IP[bestInsertIndex] > IP[i] - IP[i - 1];
          } else {
            newCandidateFound = IP[bestInsertIndex] - IP[bestInsertIndex - 1] > IP[i] - IP[i - 1];
          }
        }

        if (newCandidateFound) {
          bestInsertIndex = i;
          bestWidth = newWidth;
          bestPolys = tempPolys.slice();
        }
      }

      // we have now best poly.
      combinedPolys = bestPolys.slice();
      for (i = bestInsertIndex; i < IP.length; i++) {
        var IPval = IP[i];
        IP.splice(i, 1, IPval + 1);
      }
    }

    if (betterWidthFound) {
      var singlesIndex = 0;
      for (i = 0; i < combinedPolys.length; i++) {
        if (combinedPolys[i].single) {
          indexArr.push(initialSinglePolys[singlesIndex++].index);
        } else {
          indexArr.push(combinedPolys[i].index);
        }
      }
    }

    return betterWidthFound;
  }

  // returns the width of all polys if they were put side by side
  // from first element to last
  getJuxtaposedWidth(arr) {
    var width = 0;
    if (arr.length === 0) {
      return width;
    }

    var combinedPoly = arr[0].poly.Clone();
    for (var i = 1; i < arr.length; i++) {
      var p = arr[i].poly;
      var hDistance = combinedPoly.HorizontalDistance(p);
      if (hDistance === Number.POSITIVE_INFINITY) {
        width = hDistance;
        return width;
      }

      var delta = { x: -hDistance, y: 0 };
      p.MovePoly(delta);
      combinedPoly.Merge(combinedPoly, p);
      // Restore original position
      p.MovePoly({ x: hDistance, y: 0 });
    }

    width = combinedPoly.GetWidth();
    return width;
  }

  BalanceMultiColumn(entity, recursive) {
    // first and foremost if fAllowSort is false, this should be a no-op operation
    if (!this.fAllowSort) {
      return;
    }

    if (recursive) {
      var subs = entity.GetEntities("Subordinate");
      for (var i = 0; i < subs.length; i++) {
        this.BalanceMultiColumn(subs[i], recursive);
      }
    }

    var numColumns = entity.NumColumns();

    if (numColumns === 1) {
      this.BalanceOneColumn(entity, recursive);
    } else if (numColumns > 1) {
      var subHeights = entity.GetSubHeights();
      var sortedIndex = this.GetIndexForQuasiSort(subHeights, numColumns);
      entity.ResortSubsByIndex(sortedIndex);

      subHeights = entity.GetSubHeights();
      sortedIndex = this.GetIndexedSortByGrouping(subHeights, numColumns);
      entity.ResortSubsByIndex(sortedIndex);

      subHeights = entity.GetSubHeights();
      sortedIndex = this.GetIndexedRowSort(subHeights, numColumns);
      entity.ResortSubsByIndex(sortedIndex);
    }
  }

  BalanceOneColumn(entity, recursive) {
    // first and foremost if fAllowSort is false, this should be a no-op operation
    if (!this.fAllowSort) {
      return;
    }

    if (recursive) {
      var subs = entity.GetEntities("Subordinate");
      for (var i = 0; i < subs.length; i++) {
        this.BalanceOneColumn(subs[i], recursive);
      }
    }

    var acceptableStyles = [
      enumBranchStyle.onecolumnright,
      enumBranchStyle.onecolumncenterright,
      enumBranchStyle.onecolumnleft,
      enumBranchStyle.onecolumncenterleft
    ];

    if (
      acceptableStyles.indexOf(entity.fSubBranchStyle) === -1 ||
      entity.NumChildren("Subordinate") < 2
    ) {
      return;
    }

    var subHeights = entity.GetSubHeights();
    var sortedIndex = this.GetIndexedSortByGrouping(subHeights, 1);
    entity.ResortSubsByIndex(sortedIndex);
  }

  GetIndexedSortByGrouping(x, nGrouping) {
    if (x.length < 1) {
      return;
    }

    var xLocal = x.slice(0);

    var IndSorted = [];

    xLocal.sort(compareHeight);

    if (nGrouping > 1) {
      while (xLocal.length > nGrouping) {
        var minDiff = Number.POSITIVE_INFINITY;
        var iBest = -1;
        for (var i = 0; i < xLocal.length - nGrouping; i++) {
          if (xLocal[i + nGrouping - 1].value - xLocal[i].value < minDiff) {
            minDiff = xLocal[i + nGrouping - 1].value - xLocal[i].value;
            iBest = i;
          }
        }

        // now iBest has the minimum difference
        for (var i = iBest; i < iBest + nGrouping; i++) {
          IndSorted.push(xLocal[i].index);
        }
        xLocal.splice(iBest, nGrouping);
      }
    }

    for (var i = 0; i < xLocal.length; i++) {
      IndSorted.push(xLocal[i].index);
    }

    return IndSorted;
  }

  GetIndexForQuasiSort(x, N) {
    var IndLocal = [];
    var IndSorted = [];
    var xLocal = x.slice(0);
    for (var i = 0; i < x.length; i++) {
      IndLocal.push(i);
    }

    var xBins = [];
    var iBins = [];
    xBins.push([]);
    xBins[0].push(xLocal.shift().value);
    iBins.push([]);
    iBins[0].push(IndLocal.shift());

    var foundEqual = false;
    while (xLocal.length > 0) {
      var xTemp = xLocal.shift().value;
      var iTemp = IndLocal.shift();
      foundEqual = false;
      for (i = 0; i < xBins.length; i++) {
        if (xTemp === xBins[i][0]) {
          xBins[i].push(xTemp);
          iBins[i].push(iTemp);
          if (xBins[i].length >= N) {
            IndSorted = IndSorted.concat(iBins[i].splice(0, N));
            xBins[i].splice(0, N);
          }
          foundEqual = true;
          break;
        }
      }

      if (!foundEqual) {
        // add new element
        xBins.push([]);
        xBins[xBins.length - 1].push(xTemp);
        iBins.push([]);
        iBins[iBins.length - 1].push(iTemp);
      }
    }

    for (i = 0; i < iBins.length; i++) {
      if (iBins[i].length > 0) {
        IndSorted = IndSorted.concat(iBins[i]);
      }
    }

    return IndSorted;
  }

  ShortenEntity(entity) {
    // assumes assignment is done already
    // first make two copies and perform two types of shortening on them
    var entity1Exists = false;
    var entity2Exists = false;

    if (entity.isMultiColumnStyle()) {
      // checking next column style makes sense only if number of subs are
      var newBranchStyle = this.nextMultiColumnStyle(entity);
      if (newBranchStyle >= 0) {
        var entity1 = entity.Clone();
        entity1.SetSubBranchStyle(newBranchStyle);
        entity1.ReAssignCoordinates(true);
        this.BalanceMultiColumn(entity1, false);
        entity1.ReAssignCoordinates(false);
        entity1Exists = true;
      }
    }

    // second entity
    var iTall = this.TallestSubIndex(entity);
    if (iTall >= 0) {
      var entity2 = entity.Clone();
      entity2Exists = true;
      var tallSub = entity2.GetEntity("Subordinate", iTall);
      tallSub.ReAssignCoordinates(false);
      this.ShortenEntity(tallSub);
      entity2.ReAssignCoordinates(true);
    }
    if (entity1Exists && entity2Exists) {
      // find the shorter one and replace entity with that one
      var entity1Height = entity1.GetHeight();
      var entity2Height = entity2.GetHeight();
      if (entity1Height < entity2Height) {
        var subs = entity1.GetEntities("Subordinate").slice(0);
        entity.ReplaceSubs(subs);
      } else {
        subs = entity2.GetEntities("Subordinate").slice(0);
        entity.ReplaceSubs(subs);
      }
    } else if (entity1Exists) {
      subs = entity1.GetEntities("Subordinate").slice(0);
      entity.ReplaceSubs(subs);
    } else if (entity2Exists) {
      subs = entity2.GetEntities("Subordinate").slice(0);
      entity.ReplaceSubs(subs);
    }
  }

  /**
   * @return {number}
   */
  TallestSubIndex(entity) {
    var tallestIndex = -1;
    var maxHeight = Number.NEGATIVE_INFINITY;

    var nSubs = entity.NumChildren("Subordinate");
    for (var i = 0; i < nSubs; i++) {
      if (entity.GetEntity("Subordinate", i).GetHeight() > maxHeight) {
        maxHeight = entity.GetEntity("Subordinate", i).GetHeight();
        tallestIndex = i;
      }
    }

    return tallestIndex;
  }

  // It finds the tallest sub of entity and returns index to it.
  // It returns -1 if tallest sub does not exist (for instance entity does not have
  // any sub
  UpdateLayoutIndices(pageLayoutMgr, entity, entitySubs, recursive) {
    // Expand containers into their InsideChild entities
    var subs = [];
    for (var i = 0; i < entitySubs.length; i++) {
      var sub = entitySubs[i];
      var insideEntities = sub.GetEntities("InsideChild");
      if (insideEntities.length > 0) {
        for (var j = 0; j < insideEntities.length; j++) {
          subs.push(insideEntities[j]);
        }
      } else {
        subs.push(sub);
      }
    }

    if (recursive) {
      for (i = 0; i < subs.length; i++) {
        var subSubs = subs[i].GetEntities("Subordinate");
        this.UpdateLayoutIndices(pageLayoutMgr, subs[i], subSubs, recursive);
      }
    }

    var extendedParentChildList = this.ExtendedChildrenList(entity);
    if (extendedParentChildList.length > 0) {
      for (i = 0; i < extendedParentChildList.length; i++) {
        var item = extendedParentChildList[i];
        this.UpdateLayoutIndices(pageLayoutMgr, item.parent, item.children, recursive);
      }

      var subIDs = [];
      for (i = 0; i < entitySubs.length; i++) {
        subIDs.push(entitySubs[i].fNode.GetDataObjID());
      }
      entity.fNode.UpdateAssociateChildrenIDs(subIDs);
    } else {
      var indices = [];
      for (i = 0; i < subs.length; i++) {
        indices.push(entity.fNode.IndexOfChild(subs[i].fNode));
      }
      indices.sort(compareNumbers);

      if (subs.length === indices.length) {
        for (i = 0; i < subs.length; i++) {
          if (indices[i] >= 0) {
            pageLayoutMgr.SetChildIndex(entity.fNode, subs[i].fNode, indices[i]);
          }
        }
      }
    }
  }

  ExtendedChildrenList(entity) {
    var result = [];
    var insideChildren = entity.GetEntities("InsideChild");
    var rawSubs = entity.GetEntities("Subordinate");

    // Expand containers into their InsideChild entities
    var subs = [];
    for (var i = 0; i < rawSubs.length; i++) {
      var subInsides = rawSubs[i].GetEntities("InsideChild");
      if (subInsides.length > 0) {
        for (var j = 0; j < subInsides.length; j++) subs.push(subInsides[j]);
      } else {
        subs.push(rawSubs[i]);
      }
    }

    // Build parent ID -> subs[] map for O(1) lookup
    var subsByParentID = new Map();
    for (i = 0; i < subs.length; i++) {
      var subParent = subs[i].fNode.GetParent();
      if (subParent) {
        var pid = subParent.GetObjectID();
        if (!subsByParentID.has(pid)) subsByParentID.set(pid, []);
        subsByParentID.get(pid).push(subs[i]);
      }
    }

    for (i = 0; i < insideChildren.length; i++) {
      var insideChild = insideChildren[i];
      var insideID = insideChild.fNode.GetObjectID();
      var matched = subsByParentID.get(insideID) || [];
      var item = { parent: insideChild, children: matched, childrenIDs: [] };
      for (j = 0; j < matched.length; j++) {
        item.childrenIDs.push(matched[j].fNode.GetObjectID());
      }
      result.push(item);
    }

    return result;
  }

  /**
   * @return {number}
   Returns number of levels that are beneath entity
   If no subs it is 0
   if one level only, it returns 1
   if second level has only singles it also returns 1
   */
  GetNumberOfSubLevels(entity) {
    var nLevel = 0;
    var subs = entity.GetEntities("Subordinate");
    for (var i = 0; i < subs.length; i++) {
      var nLevelSub = this.GetNumberOfSubLevels(subs[i]);
      if (nLevel < nLevelSub) {
        nLevel = nLevelSub;
      }
      nLevel += 1;
    }

    return nLevel;
  }

  LastRowIndex(entity) {
    var rowIndex = {
      last: [],
      lastMinusOne: []
    };

    var nSubs = entity.NumChildren("Subordinate");
    if (nSubs === 0) {
      return rowIndex;
    }

    var numColumns = entity.NumColumns();
    numColumns = Math.min(numColumns, nSubs);
    var x0 = Math.floor((nSubs - 1) / numColumns) * numColumns;
    for (var x = x0; x < nSubs; x++) {
      rowIndex.last.push(x);
    }

    var nLastMinusOne = numColumns - (nSubs - x0);
    if (nLastMinusOne > 0) {
      for (x = x0 - nLastMinusOne; x < x0; x++) {
        rowIndex.lastMinusOne.push(x);
      }
    }

    return rowIndex;
  }

  // sorts array x from lowest to highest based on grouping of N
  GetIndexedRowSort(x, N) {
    var IndLocal = [];
    var IndSorted = [];
    var xLocal = [];
    for (var i = 0; i < x.length; i++) {
      IndLocal.push(i);
      xLocal.push(x[i].value);
    }

    while (xLocal.length > N) {
      var xMin = Number.POSITIVE_INFINITY;
      var iMin = -1;
      for (i = 0; i < N * Math.floor(xLocal.length / N); i += N) {
        if (xMin > Math.max.apply(null, xLocal.slice(i, i + N))) {
          xMin = xLocal[i];
          iMin = i;
        }
      }
      if (iMin < 0) {
        break;
      }

      xLocal.splice(iMin, N);
      IndSorted = IndSorted.concat(IndLocal.splice(iMin, N));
    }

    IndSorted = IndSorted.concat(IndLocal);

    return IndSorted;
  }

  LogEntity(entity, tabString) {
    var msg = tabString + entity.fID.toString() + ": " + entity.GetHeight().toString() + " > ";

    console.log(msg);

    tabString += "  ";
    for (var i = 0; i < entity.NumChildren("Subordinate"); i++) {
      this.LogEntity(entity.GetEntity("Subordinate", i), tabString);
    }
  }

  ////////////////////////////////////////////////////////////////////////////
  // These are tests that are used for pure developmental testing purposes
  ////////////////////////////////////////////////////////////////////////////
  TestReorderTreeStyle() {
    this.ReorderTreeStyle(this.fRootEntity);
    this.UpdateLayoutIndices(this.fPageLayoutMgr, this.fRootEntity, true);
  }

  TestShorten() {
    this.fRootEntity.AssignCoordinates(true, 0);
    var entity2 = this.fRootEntity.Clone();
    this.ShortenTallestSub(entity2, 1);
    this.fRootEntity = entity2.Clone();
    this.fRootEntity.AssignCoordinates(true, 0);
  }

  TestSort() {
    this.fRootEntity.AssignCoordinates(true, 0);
    var entity2 = this.fRootEntity.Clone();
    this.SortSubsByHeight(entity2, true);
    this.fRootEntity = entity2.Clone();
    this.UpdateLayoutIndices(this.fPageLayoutMgr, this.fRootEntity, true);
  }
}
